import { Mark, mergeAttributes, markInputRule, markPasteRule } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'

const LINK_URL_REGEX = /(?:^|\s)(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9@:%._+~#=/?!&]*))/gi

function isAllowedUri (url, protocols) {
	const protocol = (url || '').match(/^\s*([a-zA-Z][a-zA-Z0-9+.-]*):/)?.[1]?.toLowerCase()
	return !protocol || protocols.includes(protocol)
}

const CustomLink = Mark.create({
	name: 'link',

	priority: 1000,

	keepOnSplit: false,

	inclusive: false,

	addOptions () {
		return {
			openOnClick: false,
			linkOnPaste: true,
			autolink: true,
			protocols: ['http', 'https', 'ftp', 'mailto'],
			HTMLAttributes: {
				target: '_blank',
				rel: 'noopener noreferrer'
			},
			validate: undefined
		}
	},

	addAttributes () {
		return {
			href: {
				default: null,
				parseHTML: element => element.getAttribute('href'),
				renderHTML: attributes => {
					if (!attributes.href) {
						return {}
					}
					return { href: attributes.href }
				}
			},
			target: {
				default: this.options.HTMLAttributes.target,
				parseHTML: element => element.getAttribute('target'),
				renderHTML: attributes => {
					if (!attributes.target) {
						return {}
					}
					return { target: attributes.target }
				}
			},
			rel: {
				default: this.options.HTMLAttributes.rel,
				parseHTML: element => element.getAttribute('rel'),
				renderHTML: attributes => {
					if (!attributes.rel) {
						return {}
					}
					return { rel: attributes.rel }
				}
			},
			class: {
				default: this.options.HTMLAttributes.class || null,
				parseHTML: element => element.getAttribute('class'),
				renderHTML: attributes => {
					if (!attributes.class) {
						return {}
					}
					return { class: attributes.class }
				}
			}
		}
	},

	parseHTML () {
		return [
			{
				tag: 'a[href]:not([href *= "javascript:" i])',
				getAttrs: element => {
					const href = element.getAttribute('href')

					if (!isAllowedUri(href, this.options.protocols)) {
						return false
					}

					if (this.options.validate && !this.options.validate(href)) {
						return false
					}

					return {
						href,
						target: element.getAttribute('target'),
						rel: element.getAttribute('rel'),
						class: element.getAttribute('class')
					}
				}
			}
		]
	},

	renderHTML ({ HTMLAttributes }) {
		return [
			'a',
			mergeAttributes(
				this.options.HTMLAttributes,
				HTMLAttributes
			),
			0
		]
	},

	addCommands () {
		return {
			setLink: attributes => ({ chain }) => {
				return chain()
					.setMark(this.name, attributes)
					.run()
			},
			toggleLink: attributes => ({ chain }) => {
				return chain()
					.toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
					.run()
			},
			unsetLink: () => ({ chain }) => {
				return chain()
					.unsetMark(this.name, { extendEmptyMarkRange: true })
					.run()
			}
		}
	},

	addKeyboardShortcuts () {
		return {
			'Mod-k': () => this.editor.commands.toggleLink({ href: '' })
		}
	},

	addInputRules () {
		if (!this.options.autolink) {
			return []
		}

		return [
			markInputRule({
				find: LINK_URL_REGEX,
				type: this.type,
				getAttributes: match => ({
					href: match[1]
				})
			})
		]
	},

	addPasteRules () {
		if (!this.options.linkOnPaste) {
			return []
		}

		return [
			markPasteRule({
				find: LINK_URL_REGEX,
				type: this.type,
				getAttributes: match => ({
					href: match[1]
				})
			})
		]
	},

	addProseMirrorPlugins () {
		const plugins = []

		if (this.options.openOnClick) {
			plugins.push(
				new Plugin({
					key: new PluginKey('handleClickLink'),
					props: {
						handleClick: (view, pos, event) => {
							const link = event.target?.closest('a')

							if (link) {
								const href = link.getAttribute('href')

								if (href) {
									if (!isAllowedUri(href, this.options.protocols)) {
										return false
									}

									if (this.options.validate && !this.options.validate(href)) {
										return false
									}

									const target = link.getAttribute('target') || '_blank'
									window.open(href, target)
									return true
								}
							}

							return false
						}
					}
				})
			)
		}

		return plugins
	}
})

export { CustomLink }
